import React from 'react'
import {
  callAllHandlers,
  omit,
  useBoolean,
  useCallbackRef
} from '@mote/helpers'
import { preventBubblingEvent } from '@mote/helpers/browser'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import { VisuallyHidden } from '../visuallyHidden/visuallyHidden'
import { useCheckbox } from './checkbox.context'

const SIZES = {
  sm: { checkbox: 12, font: 14 },
  md: { checkbox: 16, font: 16 },
  lg: { checkbox: 20, font: 16 },
  xl: { checkbox: 24, font: 18 }
}

const CheckboxIcon = ({ isIndeterminate, ...props }) => (
  <svg
    width='10'
    height='8'
    viewBox='0 0 10 8'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden={true}
    focusable={false}
    role='presentation'
    {...omit(props, ['isChecked'])}
  >
    {isIndeterminate && (
      <path
        d='M9.08122 4.1905H0.999998'
        stroke='white'
        strokeWidth={1.5}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    )}
    {!isIndeterminate && (
      <>
        <path
          d='M1.57141 4.50797L3.47617 7.04766'
          stroke='white'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M9.19043 1.33336L3.47614 7.04765'
          stroke='white'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </>
    )}
  </svg>
)

export const Checkbox = ({
  checked,
  defaultChecked,
  onChange: onChangeProp,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  label,
  value = '',
  align = 'center',
  size: _size = 'xl',
  labelPosition = 'right',
  disabled = false,
  invalid = false,
  indeterminate = false,
  className,
  ...rest
}) => {
  const {
    updateState,
    inGroup,
    disabledAll,
    values,
    size: groupSize
  } = useCheckbox()
  const isDisabled = React.useMemo(
    () => disabledAll || disabled,
    [disabled, disabledAll]
  )
  const [isChecked, setIsChecked] = React.useState(!!defaultChecked)
  const [isIndeterminate, setIsIndeterminate] = React.useState(indeterminate)
  const [isFocused, isFocusedActions] = useBoolean(false)
  const hasLabel = React.useMemo(() => Boolean(label), [label])
  const size = React.useMemo(
    () => (inGroup ? groupSize : _size),
    [inGroup, groupSize, _size]
  )
  const onChange = useCallbackRef(onChangeProp)
  const onFocus = useCallbackRef(onFocusProp)
  const onBlur = useCallbackRef(onBlurProp)

  React.useEffect(() => {
    if (!inGroup) return

    const nextValue = values.includes(value)
    if (nextValue === isChecked) return
    setIsChecked(nextValue)
  }, [inGroup, isChecked, value, values])

  React.useEffect(() => {
    if (checked === undefined) return
    setIsChecked(checked)
  }, [checked])

  React.useEffect(() => {
    setIsIndeterminate(indeterminate)
  }, [indeterminate])

  const handleOnChange = React.useCallback(
    event => {
      if (isDisabled) return

      const customEvent = {
        target: {
          checked: !isChecked,
          value
        },
        stopPropagation: event.stopPropagation,
        preventDefault: event.preventDefault,
        nativeEvent: event
      }

      if (inGroup) updateState?.(value, !isChecked)

      if (isIndeterminate) {
        setIsIndeterminate(false)
        setIsChecked(true)
      } else {
        setIsChecked(!isChecked)
      }
      onChange?.(customEvent)
    },
    [
      inGroup,
      isChecked,
      isDisabled,
      isIndeterminate,
      onChange,
      updateState,
      value
    ]
  )

  const labelProps = {
    onMouseDown: preventBubblingEvent,
    onTouchStart: preventBubblingEvent
  }

  return (
    <Label disabled={isDisabled} className={className} align={align}>
      {hasLabel && labelPosition === 'left' && (
        <CheckboxText size={size} className='checkbox-text' {...labelProps}>
          {label}
        </CheckboxText>
      )}
      <CheckboxContainer
        className='checkbox-container'
        checked={isChecked || isIndeterminate}
        disabled={isDisabled}
        invalid={invalid}
        focused={isFocused}
        size={size}
        onMouseDown={event => {
          if (isFocused) {
            event.preventDefault()
          }
        }}
      >
        <VisuallyHidden
          as='input'
          type='checkbox'
          className='checkbox-input'
          disabled={isDisabled}
          checked={isChecked}
          invalid={invalid}
          aria-checked={isChecked && isIndeterminate ? 'mixed' : isChecked}
          aria-disabled={isDisabled}
          aria-invalid={invalid}
          onChange={handleOnChange}
          onFocus={callAllHandlers(onFocus, isFocusedActions.setTrue)}
          onBlur={callAllHandlers(onBlur, isFocusedActions.setFalse)}
          {...rest}
        />
        <StyledCheckboxIcon
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
        />
      </CheckboxContainer>
      {hasLabel && labelPosition === 'right' && (
        <CheckboxText size={size} className='checkbox-text' {...labelProps}>
          {label}
        </CheckboxText>
      )}
    </Label>
  )
}

if (__DEV__) {
  Checkbox.displayName = 'Checkbox'
}

const CheckboxContainer = styled.div`
  --mote-check-box-size: ${({ size }) => SIZES[size].checkbox}px;

  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--mote-check-box-size);
  height: var(--mote-check-box-size);
  min-height: var(--mote-check-box-size);
  min-width: var(--mote-check-box-size);
  padding: ${({ size }) => SIZES[size].checkbox * 0.17}px;
  border-radius: 4px;
  border: 2px solid;
  flex-shrink: 0;
  background-color: white;
  overflow: hidden;
  box-sizing: border-box;
  transition-property: box-shadow, border, background;
  transition-duration: 150ms;
  transition-timing-function: ease-in;

  ${({ focused }) =>
    focused &&
    css`
      box-shadow: outline-primary;
    `};

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: not-allowed;
        `
      : css`
          cursor: pointer;
        `}

  ${({ checked }) =>
    checked
      ? css`
          border-color: ${th.color('primary.base')};
          background-color: ${th.color('primary.base')};

          svg {
            opacity: 1;
            visibility: visible;
            transform: scale(1);
          }

          &:hover {
            border-color: ${th.color('primary.d1')};
            background-color: ${th.color('primary.d1')};
          }

          &:active {
            border-color: ${th.color('primary.d2')};
            background-color: ${th.color('primary.d2')};
          }
        `
      : css`
          border-color: ${th.color('grey.l2')};

          svg {
            opacity: 0;
            visibility: hidden;
            transform: scale(0.5);
          }
        `};

  ${({ invalid }) =>
    invalid &&
    css`
      border-color: error;

      &:hover,
      &:active {
        border-color: error;
      }
    `};

  ${({ checked, disabled }) =>
    disabled &&
    (checked
      ? css`
          border-color: ${th.color('disabled.l1')};
          background-color: ${th.color('disabled.l1')};

          &:hover,
          &:active {
            border-color: ${th.color('disabled.l1')};
            background-color: ${th.color('disabled.l1')};
          }

          path {
            stroke: ${th.color('disabled.l2')};
          }
        `
      : css`
          border-color: ${th.color('disabled.l1')};
          background-color: ${th.color('disabled.l1')};

          &:hover,
          &:active {
            border-color: ${th.color('disabled.l1')};
            background-color: ${th.color('disabled.l1')};
          }
        `)};
`

const Label = styled.label`
  position: relative;
  display: inline-flex;
  justify-content: flex-start;
  align-items: ${({ align }) => align};
  vertical-align: top;
  width: auto;
  gap: 8px;
  transition: all 150ms ease;

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: not-allowed;

          span.checkbox-text {
            opacity: 0.4;
          }
        `
      : css`
          span.checkbox-text {
            opacity: 1;
          }
        `};
`

const CheckboxText = styled.span`
  display: inline-block;
  font-size: ${({ size }) => SIZES[size].font}px;
  line-height: 120%;
  font-style: normal;
  font-weight: 400;
  color: black;
  user-select: none;
`

const StyledCheckboxIcon = styled(CheckboxIcon)`
  height: 100%;
  width: 100%;
  transition-duration: 150ms;
  transition-timing-function: ease-in;
  transition-property: transform, opacity, visibility;

  ${({ isChecked, isIndeterminate }) =>
    (isChecked || isIndeterminate) &&
    css`
      path {
        animation: move 100ms cubic-bezier(0.65, 0, 0.45, 1) 150ms forwards;
        stroke-dasharray: 4;
        stroke-dashoffset: 4;

        @keyframes move {
          100% {
            stroke-dashoffset: 0;
          }
        }
      }

      path:last-of-type:not(:first-of-type) {
        stroke-dasharray: 10;
        stroke-dashoffset: -10;
        animation-delay: 250ms;
      }

      path:only-child {
        stroke-dasharray: 8;
        stroke-dashoffset: -8;
        animation-duration: 150ms;
      }
    `};
`
